import axios from "axios";
import { useToast } from "primevue/usetoast";
import { core, internationalStreet } from "smartystreets-javascript-sdk";
import { ref, watch } from "vue";

import { leaves } from "@/api";
import { stripPhoneNumber, toRailsDateTime } from "@/utilities";
import { useForm } from "@/utilities/validations";

import { Leave, LeaveForm } from "../components/LeaveForm/types";

export function useSubmission(
  form: LeaveForm,
  requireConfirmation: boolean,
  smartystreetsKey?: string,
) {
  const isLoading = ref(false);

  const formName = "leave-form";
  const { setFieldErrorMessage, submit: formSubmit } = useForm(formName);

  const toast = useToast();

  const showError = () =>
    toast.add({
      severity: "error",
      summary: "Errors in form",
      detail: "You seem to have a few errors in your form",
      life: 5000,
    });

  const confirmationDialog = ref(false);

  const handleError = (err: Error) => {
    if (axios.isAxiosError(err) && err.response) {
      Object.entries(err.response.data).forEach(([key, val]) => {
        if (key === "leaveReturn") {
          setFieldErrorMessage("returnDate", (val as string[]).join(", "));
          setFieldErrorMessage("returnTime", (val as string[]).join(", "));
          return;
        }

        if (key === "leaveDepart") {
          setFieldErrorMessage("departDate", (val as string[]).join(", "));
          setFieldErrorMessage("departTime", (val as string[]).join(", "));
          return;
        }

        setFieldErrorMessage(key, (val as string[]).join(", "));
      });
      showError();
    }
  };

  const vouchedForAddress = ref(false);
  const addressVerificationDialogOpen = ref(false);

  watch(
    [() => form.address, () => form.city, () => form.state, () => form.postal],
    () => {
      vouchedForAddress.value = false;
    },
  );

  const verifyAddress = async (form: LeaveForm) => {
    if (smartystreetsKey == null) {
      return true;
    }

    const credentials = new core.SharedCredentials(smartystreetsKey);
    const clientBuilder = new core.ClientBuilder(credentials).withLicenses([
      "international-global-plus-cloud",
    ]);

    const client = clientBuilder.buildInternationalStreetClient();

    const Lookup = internationalStreet.Lookup;

    const lookup = new Lookup(
      "USA",
      `${form.address}, ${form.city}, ${form.state}, ${form.postal}`,
    );

    try {
      const result = await client.send(lookup);

      if (result.result.length === 0) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  const submit = formSubmit(async () => {
    if (!vouchedForAddress.value && !(await verifyAddress(form))) {
      addressVerificationDialogOpen.value = true;
      return;
    }

    if (requireConfirmation && !confirmationDialog.value) {
      confirmationDialog.value = true;
      return;
    }

    isLoading.value = true;

    const data = convertToLeave(form);

    try {
      const response = await leaves.update({
        params: { id: data.leaveId },
        data,
      });

      window.location.assign(response.location);
    } catch (err) {
      handleError(err as Error);
    } finally {
      isLoading.value = false;
    }
  }, showError);

  return {
    submit,
    confirmationDialog,
    isLoading,
    formName,
    vouchedForAddress,
    addressVerificationDialogOpen,
  };
}

function convertToLeave(form: LeaveForm): Leave {
  let leaveDepart = null;
  let leaveReturn = null;
  if (
    form.departDate &&
    form.departTime &&
    form.returnDate &&
    form.returnTime
  ) {
    leaveDepart = toRailsDateTime(
      form.departDate.set({
        hour: form.departTime.hour,
        minute: form.departTime.minute,
        second: form.departTime.second,
      }),
    );
    leaveReturn = toRailsDateTime(
      form.returnDate.set({
        hour: form.returnTime.hour,
        minute: form.returnTime.minute,
        second: form.returnTime.second,
      }),
    );
  }

  return {
    ...form,
    leaveDepart: leaveDepart ?? "",
    leaveReturn: leaveReturn ?? "",
    hostphone: stripPhoneNumber(form.hostphone) ?? "",
    cellphone: stripPhoneNumber(form.cellphone) ?? "",
  };
}
